import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Activity, Coins, X } from 'lucide-react';
import { useUserAdvanceTokenLedger } from '../hooks/useUsers';
import type { User, UserUsageGranularity } from '../types';
import { formatAdminNumber } from '../../../../utils/numberFormat';

type LedgerDirection = 'credit' | 'debit';

interface AdvanceTokenLedgerModalProps {
  open: boolean;
  user: User | null;
  direction: LedgerDirection;
  onClose: () => void;
}

interface ChartPoint {
  key: string;
  label: string;
  amount: number;
}

const GRANULARITY_OPTIONS: Array<{ value: UserUsageGranularity; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

const shouldShowLabel = (index: number, total: number) =>
  total <= 6 || index === 0 || index === total - 1 || index % Math.ceil(total / 4) === 0;

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatReason = (reason: string) =>
  reason
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';

const SingleSeriesTokenChart: React.FC<{
  data: ChartPoint[];
  direction: LedgerDirection;
}> = ({ data, direction }) => {
  const color = direction === 'credit' ? '#f59e0b' : '#0ea5e9';
  const total = data.reduce((sum, point) => sum + point.amount, 0);
  const maxAmount = Math.max(1, ...data.map((point) => point.amount));
  const chartLeft = 64;
  const chartRight = 32;
  const chartTop = 24;
  const chartHeight = 220;
  const chartBottom = 56;
  const groupWidth = 72;
  const width = Math.max(data.length * groupWidth + chartLeft + chartRight, 680);
  const height = chartTop + chartHeight + chartBottom;
  const chartWidth = width - chartLeft - chartRight;
  const baseline = chartTop + chartHeight;
  const pointStep = data.length > 1 ? chartWidth / (data.length - 1) : chartWidth;
  const barWidth = Math.max(10, Math.min(28, (chartWidth / Math.max(data.length, 1)) * 0.32));
  const getX = (index: number) =>
    data.length > 1 ? chartLeft + index * pointStep : chartLeft + chartWidth / 2;
  const getY = (value: number) => baseline - (value / maxAmount) * chartHeight;
  const linePoints = data.map((point, index) => `${getX(index)},${getY(point.amount)}`).join(' ');
  const label = direction === 'credit' ? 'Granted' : 'Used';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{label} Trend</h3>
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
          <span
            className="h-0.5 w-5 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <span>
            {label}: {formatAdminNumber(total)}
          </span>
        </div>
      </div>

      {data.length ? (
        <div className="overflow-x-auto pb-1">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            style={{ width: '100%', minWidth: `${width}px`, height: `${height}px` }}
            role="img"
            aria-label={`Advance credits ${label.toLowerCase()} chart`}
          >
            <text
              x={chartLeft}
              y={12}
              textAnchor="start"
              className="fill-gray-400 text-[11px] font-medium dark:fill-slate-500"
            >
              Tokens
            </text>
            {[0, 0.5, 1].map((ratio) => {
              const y = baseline - ratio * chartHeight;

              return (
                <g key={ratio}>
                  <line
                    x1={chartLeft}
                    y1={y}
                    x2={width - chartRight}
                    y2={y}
                    stroke="currentColor"
                    className="text-gray-200 dark:text-slate-700"
                    strokeWidth="1"
                  />
                  <text
                    x={chartLeft - 10}
                    y={y + 4}
                    textAnchor="end"
                    className="fill-gray-400 text-[11px] dark:fill-slate-500"
                  >
                    {formatAdminNumber(Math.round(maxAmount * ratio))}
                  </text>
                </g>
              );
            })}
            <line
              x1={chartLeft}
              y1={baseline}
              x2={width - chartRight}
              y2={baseline}
              stroke="currentColor"
              className="text-gray-300 dark:text-slate-600"
              strokeWidth="1"
            />
            {data.map((point, index) => {
              const x = getX(index);
              const barHeight = (point.amount / maxAmount) * chartHeight;

              return (
                <g key={point.key}>
                  <rect
                    x={x - barWidth / 2}
                    y={baseline - barHeight}
                    width={barWidth}
                    height={Math.max(point.amount > 0 ? 2 : 0, barHeight)}
                    rx="3"
                    fill={color}
                    opacity={point.amount > 0 ? 0.22 : 0.08}
                  />
                  {shouldShowLabel(index, data.length) && (
                    <text
                      x={x}
                      y={baseline + 24}
                      textAnchor="middle"
                      className="fill-gray-500 text-[11px] dark:fill-slate-400"
                    >
                      {point.label}
                    </text>
                  )}
                </g>
              );
            })}
            {data.length > 1 && (
              <polyline
                points={linePoints}
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {data.map((point, index) => {
              const x = getX(index);
              const y = getY(point.amount);

              return (
                <g key={`${point.key}-point`}>
                  <circle cx={x} cy={y} r="4" fill={color} stroke="#ffffff" strokeWidth="2">
                    <title>{`${point.label}: ${formatAdminNumber(point.amount)} credits`}</title>
                  </circle>
                  <text
                    x={x}
                    y={Math.max(chartTop + 12, y - 10)}
                    textAnchor="middle"
                    className="fill-gray-700 text-[10px] font-semibold dark:fill-slate-200"
                  >
                    {formatAdminNumber(point.amount)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      ) : (
        <div className="flex h-72 items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          No chart data
        </div>
      )}
    </div>
  );
};

export const AdvanceTokenLedgerModal: React.FC<AdvanceTokenLedgerModalProps> = ({
  open,
  user,
  direction,
  onClose,
}) => {
  const [activeDirection, setActiveDirection] = useState<LedgerDirection>(direction);
  const [granularity, setGranularity] = useState<UserUsageGranularity>('day');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { data, isLoading, isFetching, isError, error } = useUserAdvanceTokenLedger(
    open ? user?.publicId : undefined,
    activeDirection,
    page,
    pageSize
  );

  useEffect(() => {
    if (open) {
      setActiveDirection(direction);
      setGranularity('day');
      setPage(1);
      setPageSize(20);
    }
  }, [direction, open, user?.publicId]);

  const chartData = useMemo(() => data?.charts[granularity] ?? [], [data?.charts, granularity]);
  const displayName = user?.userName || user?.email || 'User';
  const totalLabel = activeDirection === 'credit' ? 'Total Granted' : 'Total Used';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(15, 23, 42)' : 'white'),
          color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <div className="flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-gray-900 dark:text-white">
              {displayName}
            </p>
            <p className="truncate text-sm font-normal text-gray-500 dark:text-slate-400">
              Advance token ledger
            </p>
          </div>
          <IconButton
            aria-label="Close advance credit ledger"
            onClick={onClose}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            }}
          >
            <X size={20} />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <div className="mb-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={activeDirection}
            onChange={(_, value: LedgerDirection | null) => {
              if (value) {
                setActiveDirection(value);
                setPage(1);
              }
            }}
            aria-label="Advance credit ledger direction"
          >
            <ToggleButton value="credit">
              <Coins size={15} className="mr-1.5" />
              Granted
            </ToggleButton>
            <ToggleButton value="debit">
              <Activity size={15} className="mr-1.5" />
              Used
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={granularity}
            onChange={(_, value: UserUsageGranularity | null) => {
              if (value) {
                setGranularity(value);
              }
            }}
            aria-label="Advance credit ledger chart granularity"
          >
            {GRANULARITY_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        {isLoading ? (
          <div className="flex min-h-80 items-center justify-center">
            <CircularProgress sx={{ color: '#3B82F6' }} />
          </div>
        ) : isError ? (
          <Alert severity="error">{error?.message || 'Unable to load advance credit ledger.'}</Alert>
        ) : data ? (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-slate-400">
                  {activeDirection === 'credit' ? (
                    <Coins size={17} className="text-amber-500" />
                  ) : (
                    <Activity size={17} className="text-sky-500" />
                  )}
                  {totalLabel}
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                  {formatAdminNumber(data.summary.total)}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Ledger Entries
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                  {formatAdminNumber(data.entries.total)}
                </div>
              </div>
            </div>

            <div className="flex min-h-5 justify-end">
              {isFetching && (
                <span className="text-sm text-gray-500 dark:text-slate-400">Refreshing...</span>
              )}
            </div>

            <SingleSeriesTokenChart data={chartData} direction={activeDirection} />

            <TableContainer className="rounded-lg border border-gray-200 dark:border-slate-700">
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'rgb(249, 250, 251)',
                    }}
                  >
                    {['Reason', 'Amount', 'Balance After', 'Date'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgb(148, 163, 184)'
                              : 'rgb(55, 65, 81)',
                          fontWeight: 600,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.entries.items.length ? (
                    data.entries.items.map((entry) => (
                      <TableRow key={entry.publicId}>
                        <TableCell className="dark:border-slate-700">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatReason(entry.reason)}
                          </span>
                        </TableCell>
                        <TableCell className="dark:border-slate-700">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatAdminNumber(entry.tokenAmount)}
                          </span>
                        </TableCell>
                        <TableCell className="dark:border-slate-700">
                          <span
                            className={
                              entry.balanceAfter < 0
                                ? 'font-semibold text-amber-600 dark:text-amber-300'
                                : 'text-gray-700 dark:text-slate-300'
                            }
                          >
                            {formatAdminNumber(entry.balanceAfter)}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-600 dark:border-slate-700 dark:text-slate-400">
                          {formatDateTime(entry.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">
                          No ledger entries
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={data.entries.total}
                page={Math.max(page - 1, 0)}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                onRowsPerPageChange={(event) => {
                  setPageSize(Number.parseInt(event.target.value, 10));
                  setPage(1);
                }}
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
                  borderTop: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '1px solid rgb(51, 65, 85)'
                      : '1px solid rgb(229, 231, 235)',
                }}
              />
            </TableContainer>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
